import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Globe, ShieldCheck, Zap } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { SHOP_URL, IMAGES, GUIDEBOOKS } from "../data/content";

const HIGHLIGHTS = [
  { icon: Zap, title: "Instant digital delivery", desc: "Digital volumes arrive by email the moment checkout completes." },
  { icon: BookOpen, title: "Archival print quality", desc: "Field manuals printed to museum-grade standards, bound to last." },
  { icon: ShieldCheck, title: "Encrypted checkout", desc: "Fourthwall Payments — Stripe, Apple Pay, and PayPal supported." },
  { icon: Globe, title: "Global tracked dispatch", desc: "Print editions ship worldwide with tracking on every order." },
];

export default function Shop() {
  usePageMeta(
    "Shop — The Official Fourthwall Storefront | Consciousness Ascending",
    "All guidebooks, workbooks and codexes are sold through our official Fourthwall shop. Secure checkout, worldwide shipping, instant digital delivery."
  );
  return (
    <>
      <PageHero
        eyebrow="The Official Storefront"
        lines={["Your path,", "printed & bound."]}
        description="Direct access to physical guidebooks, digital volumes, and educational codexes. Fourthwall is the only place we sell — every purchase supports the work directly."
        image={IMAGES.shop}
        imageAlt="The gateway storefront"
        imageAspect="aspect-[4/3]"
        meta="consciousness-ascending-shop.fourthwall.com"
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-10 lg:py-24">
        <Reveal>
          <a
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-destination-primary-btn"
            className="group relative flex flex-col items-start justify-between gap-6 overflow-hidden border border-[#8B5CF6] bg-[#8B5CF6]/10 p-8 transition-all duration-500 hover:bg-[#8B5CF6]/20 hover:shadow-[0_0_44px_rgba(139,92,246,0.35)] sm:flex-row sm:items-center sm:p-10"
          >
            <div>
              <p className="eyebrow !text-[10px]">Primary Destination</p>
              <p className="font-display mt-3 text-2xl text-[#F0F2F8] sm:text-3xl">
                Open the Fourthwall Shop in a new window
              </p>
              <p className="mt-2 font-mono-ui text-[11px] tracking-[0.14em] text-[#38BDF8]">
                consciousness-ascending-shop.fourthwall.com
              </p>
            </div>
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#8B5CF6]/60 transition-transform duration-300 group-hover:rotate-45">
              <ArrowUpRight className="h-6 w-6 text-[#A78BFA]" />
            </span>
          </a>
        </Reveal>

        <div className="mt-16 grid gap-px border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-highlights">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={0.08 * i} className="bg-[#0A0C12] p-7 lg:p-8">
              <h.icon className="h-5 w-5 text-[#38BDF8]" aria-hidden="true" />
              <h3 className="mt-6 text-sm text-[#F0F2F8]">{h.title}</h3>
              <p className="body-muted mt-3 text-sm leading-relaxed">{h.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-[#0C0E14]/40">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-28">
          <Reveal>
            <p className="eyebrow">What You Will Find</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl">
              The current catalogue.
            </h2>
          </Reveal>
          <div className="mt-14 border-t border-white/[0.07]">
            {GUIDEBOOKS.map((book, i) => (
              <Reveal key={book.id} delay={0.05 * i}>
                <a
                  href={SHOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shop-catalog-link-${book.id}`}
                  className="group grid gap-2 border-b border-white/[0.07] py-7 transition-colors duration-300 hover:bg-white/[0.02] sm:grid-cols-12 sm:items-center sm:gap-6 sm:px-4"
                >
                  <p className="font-mono-ui text-[11px] tracking-[0.2em] text-[#38BDF8] sm:col-span-1">
                    {book.coord}
                  </p>
                  <div className="sm:col-span-6">
                    <h3 className="font-display text-xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-2xl">
                      {book.title}
                    </h3>
                  </div>
                  <p className="text-[10px] font-mono-ui uppercase tracking-[0.2em] text-[#8E95A5] sm:col-span-3">
                    {book.category}
                  </p>
                  <span className="inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#A78BFA] sm:col-span-2 sm:justify-end">
                    View in shop
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/[0.07]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:px-10 lg:py-20">
          <Reveal>
            <p className="font-display text-2xl font-light tracking-tight text-[#F0F2F8] sm:text-3xl">
              Not ready to shop? Learn first.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/remote-viewing"
                data-testid="shop-learn-rv-btn"
                className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-[#8B5CF6]"
              >
                Remote Viewing <ArrowRight className="h-3.5 w-3.5 text-[#A78BFA]" />
              </Link>
              <Link
                to="/astral-projection"
                data-testid="shop-learn-ap-btn"
                className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#F0F2F8] transition-all duration-300 hover:border-[#8B5CF6]"
              >
                Astral Projection <ArrowRight className="h-3.5 w-3.5 text-[#A78BFA]" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
